import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import IconFont from '$/components/IconFont';
import { message } from '$/components/Message';
import { getCommunityCategory, getCommunityList, getCommunityUsedList, getCommunityPublishedList } from '$/domains/community/communityService';
import { CommunityCardItem, CommunityCategoryItem } from '$/domains/community/type';
import { useLanguageStore, replaceTemplateWithElements } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { getUrlSearchParams } from '$/utils';
import { useInfiniteScroll } from 'ahooks';
import { useCommonStore } from '@/store/useCommonStore';
import { createNewFlow } from '@/components/Navbar';
import { PlayStateChangeTrigger } from '$/components/AudioPlayer';
import styles from './index.module.scss';
import WorkItem from './WorkItem';

const LIST_COUNT = 20;

export enum CategoryItemId {
  used = 'used',
  mine = 'mine',
}

function CommunityContent() {
  const searchParams = getUrlSearchParams();
  const activeTabByUrl = searchParams.get('active_tab');

  const languageStore = useLanguageStore((state) => state.languageStore);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const toLogin = useCommonStore((state) => state.toLogin);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);

  const listBoxRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<number>(1);
  const [activeTabId, setActiveTabId] = useState<number | string>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [workList, setWorkList] = useState<CommunityCardItem[]>([]);
  const [categoryList, setCategoryList] = useState<CommunityCategoryItem[]>([]);
  const [currentAudioPlayingId, setCurrentAudioPlayingId] = useState<number | null>(null);
  const CategoryItem = [
    {
      category_id: CategoryItemId.used,
      name: languageStore.smartcatTranslationText((v) => v.roboneo_web_my_used),
      iconfont: 'clockBold',
    },
    {
      category_id: CategoryItemId.mine,
      name: languageStore.smartcatTranslationText(
        (v) => v.roboneo_web_my_works,
      ),
      iconfont: 'paperAirplaneBold',
    },
  ];

  // 获取分类
  useEffect(() => {
    getCommunityCategory().then((res) => {
      setCategoryList(res.list.concat(CategoryItem));
      // 设置选中的分类
      if (
        activeTabByUrl
        && (activeTabByUrl === CategoryItemId.used
          || activeTabByUrl === CategoryItemId.mine)
      ) {
        setActiveTabId(activeTabByUrl);
      } else {
        setActiveTabId(res.list?.[0]?.category_id || 0);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabByUrl]);

  // 退出登录时，切换到第一个分类
  useEffect(() => {
    if (
      !isUserLoggedIn
      && (activeTabId === CategoryItemId.used
        || activeTabId === CategoryItemId.mine)
    ) {
      setWorkList([]);
      handleTabClickCallValue(categoryList[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoggedIn]);

  const { loading } = useInfiniteScroll(
    () => {
      if (!hasMore) return Promise.resolve({ list: [] as CommunityCardItem[] });

      // 根据activeTabId的值选择不同的API函数
      let requestPromise: Promise<{ list: CommunityCardItem[]; }>;

      if (activeTabId === CategoryItemId.used) {
        // 请求我用过的作品列表
        requestPromise = getCommunityUsedList({
          page: pageRef.current,
          count: LIST_COUNT,
        });
      } else if (activeTabId === CategoryItemId.mine) {
        // 请求我的作品列表
        requestPromise = getCommunityPublishedList({
          page: pageRef.current,
          count: LIST_COUNT,
        });
      } else {
        // 其他情况请求通用社区列表
        requestPromise = getCommunityList({
          page: pageRef.current,
          count: LIST_COUNT,
          category_id: activeTabId as number,
        });
      }

      // 统一处理返回结果
      return requestPromise.then((res) => {
        if (res?.list?.length === 0) setHasMore(false);
        return res;
      });
    },
    {
      manual: true,
      target: listBoxRef,
      isNoMore: (d) => d?.list?.length === 0 || !hasMore,
      onSuccess: (d) => {
        if (d?.list?.length > 0) {
          pageRef.current += 1;
          // 数据合并到 workList state
          setWorkList((prev) => [...prev, ...d.list]);
        } else {
          setHasMore(false);
        }
      },
      onError: () => message.error('获取失败'),
      reloadDeps: [activeTabId],
    },
  );

  // 切换tab
  const handleTabClick = async (item: CommunityCategoryItem, index: number) => {
    if (item.category_id === activeTabId) return;

    window.mtstat?.track('roboneo_community_tab_click', {
      tab_id: item.category_id,
      tab_name: item.name,
      position: index + 1,
    });

    // 如果是我用过的，我的作品，未登录，则跳转到登录
    if (
      (item.category_id === CategoryItemId.used
        || item.category_id === CategoryItemId.mine)
      && !isUserLoggedIn
    ) {
      await toLogin().then(() => {
        initUserInfo();
      });
      handleTabClickCallValue(item);
      return;
    }
    handleTabClickCallValue(item);
  };
  // 触发 tab 点击
  const handleTabClickCallValue = (item: CommunityCategoryItem) => {
    pageRef.current = 1;
    setHasMore(true);
    setActiveTabId(item.category_id);
    setWorkList([]);
  };

  // 计算列数，并将列数设置到 CSS 变量 --cols 上
  useEffect(() => {
    const listEl = document.querySelector(
      `.${styles.listBox}`,
    ) as HTMLElement | null;
    if (!listEl) return;

    const GAP = 8; // 与样式保持一致
    const MIN_W = 290; // 最小宽度
    const MAX_W = 380; // 最大宽度

    const calcAndApplyCols = () => {
      const containerWidth = listEl.clientWidth;
      if (!containerWidth) return;

      // 基于最小宽度尽可能多地排布列数（确保每列宽度不小于 MIN_W）
      let cols = Math.max(
        1,
        Math.floor((containerWidth + GAP) / (MIN_W + GAP)),
      );

      // 计算列下的实际 item 宽度
      let itemWidth = Math.floor((containerWidth - GAP * (cols - 1)) / cols);

      // 如果依然明显大于最大宽度，尝试继续增加列数以避免卡片过宽
      while (itemWidth > MAX_W) {
        const nextCols = cols + 1;
        const nextItemWidth = Math.floor(
          (containerWidth - GAP * (nextCols - 1)) / nextCols,
        );
        if (nextItemWidth < MIN_W || nextCols > 24) break; // 保护：不要低于最小宽
        cols = nextCols;
        itemWidth = nextItemWidth;
      }

      listEl.style.setProperty('--cols', String(cols));
    };

    calcAndApplyCols();
    const onResize = () => calcAndApplyCols();
    window.addEventListener('resize', onResize);
    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('resize', onResize);
  }, [workList.length]);

  const handleAudioPlayStateChange = (data: { playing: boolean; trigger: PlayStateChangeTrigger; communityId: number; }) => {
    const { playing, trigger, communityId } = data;
    if (trigger === PlayStateChangeTrigger.ExternalStop) {
      return;
    }
    if (playing) {
      setCurrentAudioPlayingId(communityId);
    } else {
      setCurrentAudioPlayingId(null);
    }
  };

  return (
    <div className={styles.contentBox}>
      <div className={styles.titleBox}>
        <h3>
          {languageStore.smartcatTranslationText(
            (v) => v.roboneo_web_works_community,
          )}
        </h3>
      </div>
      {/* 分类 */}
      {!!categoryList.length && (
        <div className={styles.tabBox}>
          {categoryList.map((item: CommunityCategoryItem, index: number) => (
            <div
              key={item.category_id}
              className={classNames(
                styles.tabItem,
                activeTabId === item.category_id && styles.active,
              )}
              onClick={() => handleTabClick(item, index)}
            >
              {item.iconfont && (
                <IconFont
                  name={item.iconfont}
                  fontSize={16}
                  className={styles.icon}
                />
              )}
              <span>{item.name}</span>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className={styles.loadingBox}>
          <IconFont
            name="loadingCircleBold"
            fontSize={28}
            className={styles.loadingIcon}
          />
          <span>
            {languageStore.smartcatTranslationText(
              (v) => v.RoboNeo_Web_loading,
            )}
          </span>
        </div>
      ) : (
        <div className={styles.workContentBox}>
          {workList.length === 0 ? (
            <div className={styles.emptyBox}>
              <IconFont
                name="drawerBold"
                fontSize={40}
                className={styles.emptyIcon}
              />
              <div className={styles.emptyText}>
                {
                  // eslint-disable-next-line no-nested-ternary
                  activeTabId === CategoryItemId.used ? (
                    <>
                      {replaceTemplateWithElements(
                        'roboneo_web_no_used_cases_tips',
                        {
                          work_community: (
                            <span
                              className={styles.link}
                              onClick={() => {
                                handleTabClickCallValue(categoryList[0]);
                              }}
                            >
                              {languageStore.smartcatTranslationText(
                                (v) => v.roboneo_web_works_community,
                              )}
                            </span>
                          ),
                        },
                      )}
                    </>
                  ) : activeTabId === CategoryItemId.mine ? (
                    <>
                      {replaceTemplateWithElements(
                        'roboneo_web_no_works_create_workflow',
                        {
                          workflow_goto: (
                            <span
                              className={styles.link}
                              onClick={() => {
                                createNewFlow('community_no_content');
                              }}
                            >
                              {languageStore.smartcatTranslationText(
                                (v) => v.roboneo_web_workflow,
                              )}
                            </span>
                          ),
                        },
                      )}
                    </>
                  ) : (
                    <>
                      {languageStore.smartcatTranslationText(
                        (v) => v.Roboneo_web_no_data,
                      )}
                    </>
                  )
                }
              </div>
            </div>
          ) : (
            <div className={styles.listBox} ref={listBoxRef}>
              {workList.map((item: CommunityCardItem, index: number) => (
                <WorkItem
                  stopAudioPlaying={item.community_id !== currentAudioPlayingId}
                  key={item.community_id}
                  item={item}
                  activeTabId={activeTabId}
                  activeTabName={
                    categoryList.find(
                      (category) => category.category_id === activeTabId,
                    )?.name
                  }
                  currentIndex={index}
                  onAudioPlayStateChange={(data) => handleAudioPlayStateChange({ ...data, communityId: item.community_id })}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CommunityContent;
